import { useEffect, useRef, useState } from "react";
import { Layout } from "../components/Layout";
import { cachedGet, videoSrc } from "../lib/api";
import { Play, AlertCircle, ExternalLink } from "lucide-react";

/**
 * Converts a YouTube watch URL or youtu.be short URL to an embed URL.
 * Returns null if the URL is not a recognised YouTube format.
 */
function youtubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1` : null;
}

export default function ArchivePage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    cachedGet("/videos", (data) => {
      if (!mounted) return;
      setVideos(data);
      setLoading(false);
    })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Layout>
      <section
        className="min-h-screen pt-28 md:pt-32 pb-24 px-5 md:px-8"
        data-testid="archive-page"
      >
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-14 md:mb-20 fade-up">
            <p className="text-yellow-400 text-[11px] tracking-[0.45em] uppercase mb-4">
              Campaign Archive
            </p>
            <h1
              className="text-5xl md:text-7xl font-semibold text-white tracking-tight leading-[1.02]"
              data-testid="archive-title"
            >
              2025 Campaign
              <br />
              <span className="italic text-yellow-400">in motion.</span>
            </h1>
            <p className="mt-6 text-white/55 max-w-xl text-base md:text-lg leading-relaxed">
              A look back at the videos that defined the run for Vineyard City
              Council — from the first announcement to the final message before
              election day.
            </p>
          </div>

          {/* Videos */}
          {loading ? (
            <div className="skeleton rounded-3xl aspect-video" data-testid="archive-loading" />
          ) : videos.length === 0 ? (
            <p className="text-white/40">No videos yet.</p>
          ) : (
            <div className="space-y-16 md:space-y-24" data-testid="archive-list">
              {videos.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const [near, setNear] = useState(index === 0);
  const [ready, setReady] = useState(false);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const num = String(index + 1).padStart(2, "0");

  const embedUrl = youtubeEmbedUrl(video.video_url);
  const src = video.video_url && !embedUrl ? video.video_url : videoSrc(video);

  // Only start fetching a video when it's about to scroll into view, so the
  // first one isn't competing with every other video on the page.
  useEffect(() => {
    if (near || embedUrl) return;
    const el = cardRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setNear(true),
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, embedUrl]);

  const play = () => {
    setPlaying(true);
    if (embedUrl) return;
    setNear(true);
    // Same element that already buffered the preview: playback starts at once
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };

  return (
    <article
      ref={cardRef}
      className="group fade-up"
      style={{ animationDelay: `${Math.min(index, 4) * 80}ms` }}
      data-testid={`video-card-${video.id}`}
    >
      {/* Eyebrow */}
      <div className="flex items-baseline gap-4 mb-3">
        <span className="text-yellow-400/70 text-xs tracking-[0.35em] font-medium">
          {num}
        </span>
        <span className="text-white/45 text-[11px] uppercase tracking-[0.32em]">
          {video.subtitle || "Campaign Video"}
        </span>
      </div>

      <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight mb-3 leading-tight">
        {video.title}
      </h2>
      {video.description && (
        <p className="text-white/60 max-w-2xl text-base mb-6 leading-relaxed">
          {video.description}
        </p>
      )}

      {/* Player */}
      <div
        className={`relative rounded-3xl overflow-hidden border-[0.5px] border-white/15 shadow-2xl shadow-black/40 aspect-video ${
          ready || playing || video.poster_url ? "bg-black" : "skeleton"
        }`}
        data-testid={`video-player-${video.id}`}
      >
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-center p-6">
            <AlertCircle className="mb-3 text-red-400" size={36} />
            <p className="text-white/70 text-sm mb-4">
              This video couldn't be loaded right now. Please try again shortly.
            </p>
            {video.video_url && (
              <a
                href={video.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent inline-flex items-center gap-2 px-5 py-2.5 text-sm"
              >
                <ExternalLink size={14} /> Watch Externally
              </a>
            )}
          </div>
        ) : embedUrl ? (
          playing && (
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={video.title}
              data-testid={`video-el-${video.id}`}
            />
          )
        ) : (
          <video
            ref={videoRef}
            src={near ? src : undefined}
            poster={video.poster_url || undefined}
            className={`absolute inset-0 w-full h-full ${playing ? "object-contain" : "object-cover"}`}
            controls={playing}
            playsInline
            autoPlay={playing}
            preload={near ? "metadata" : "none"}
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            onLoadedData={() => setReady(true)}
            onPlay={(event) => {
              document.querySelectorAll("video").forEach((other) => {
                if (other !== event.currentTarget) other.pause();
              });
            }}
            onError={() => near && setError(true)}
            data-testid={`video-el-${video.id}`}
          />
        )}

        {!playing && !error && (
          <button
            type="button"
            onClick={play}
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            aria-label={`Play ${video.title}`}
            data-testid={`video-play-${video.id}`}
          >
            <div className={`absolute inset-0 ${video.poster_url ? "bg-black/5" : "bg-gradient-to-t from-[#04212f]/90 via-[#04212f]/20 to-transparent"}`} />

            {/* Title overlay */}
            {!video.poster_url && <div className="absolute inset-x-0 bottom-0 px-6 md:px-10 pb-6 md:pb-10 text-left z-10">
              <p className="text-yellow-400/90 text-[10px] tracking-[0.35em] uppercase mb-2">
                {video.subtitle}
              </p>
              <p className="text-white text-2xl md:text-4xl font-semibold tracking-tight leading-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]">
                {video.title}
              </p>
            </div>}

            {/* Play badge — frosted, Apple TV-style */}
            <div className="relative z-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/20 backdrop-blur-xl border-[0.5px] border-white/40 text-white flex items-center justify-center shadow-[0_20px_60px_-12px_rgba(0,0,0,0.6)] group-hover:scale-105 group-hover:bg-white/30 transition-all">
              <Play size={32} className="fill-current ml-1" />
            </div>
          </button>
        )}
      </div>
    </article>
  );
};
